/* ==========================================================================
   Ozeret — pictures

   A photo of the inside of somebody's home is the most sensitive thing this
   app holds, so two rules are worth stating where the code is rather than
   only in an issue:

   1. Only a home ever attaches a photo of a home. A cleaner cannot, anywhere
      — not on a job, not in a chat. The only picture a cleaner adds is their
      own face. If a home wants to see something, that is a phone call.
   2. A photo stays on the record. It is part of a job's history, not a cache,
      and it survives the visit being finished.

   The server enforces both; nothing here is load-bearing for security. What
   this file does is shrink the picture before it leaves the phone, which is
   worth doing for three separate reasons: an 8MP photo over a phone signal is
   slow, the database is the backup and it should not fill with camera
   originals, and re-encoding drops the EXIF block — a photo of somebody's
   kitchen should not arrive carrying the coordinates of their kitchen.
   ========================================================================== */

import { useEffect, useRef, useState } from 'react';
import { t } from '../lib/i18n';
import { api, photoUrl, type Photo, type PhotoKind } from '../api';
import { Icon } from './Icon';

/** Long edge, in pixels. Big enough to see which cupboard is meant on a
 *  laptop; small enough that a photo is tens of kilobytes, not megabytes. */
const MAX_EDGE = 1600;
const QUALITY = 0.82;

/** What came off the camera, made smaller and stripped of its metadata.
 *
 *  Rotation is handled by the browser: createImageBitmap applies the EXIF
 *  orientation, so a photo taken sideways is upright by the time it is drawn,
 *  and the tag it came with is gone along with everything else. */
export async function shrink(file: File): Promise<{ dataUrl: string; width: number; height: number }> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot resize images.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  return { dataUrl: canvas.toDataURL('image/jpeg', QUALITY), width, height };
}

/* --------------------------------------------------------------- viewing */

/** The photos on one thing, as a row of thumbnails. Tapping one opens it.
 *
 *  `canAdd` is the caller saying whether this person is allowed to attach —
 *  a cleaner is passed false everywhere. Getting it wrong shows a button that
 *  the server then refuses, which is a poor experience but not a hole. */
export function PhotoStrip({
  kind, ownerId, canAdd, label,
}: {
  kind: PhotoKind; ownerId: string; canAdd?: boolean; label?: string;
}) {
  const [photos, setPhotos] = useState<Photo[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const [open, setOpen] = useState<Photo | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    api.listPhotos(kind, ownerId)
      .then((r) => { if (alive) setPhotos(r.photos); })
      /* Not allowed to see them is a normal answer here, not a failure worth
         showing: it simply means there is nothing on this screen for you. */
      .catch(() => { if (alive) setPhotos([]); });
    return () => { alive = false; };
  }, [kind, ownerId]);

  const add = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setFailed('');
    try {
      const added: Photo[] = [];
      /* One at a time. Six photos uploading at once from a phone on a weak
         signal is how you get six failures instead of one. */
      for (const file of Array.from(files).slice(0, 6)) {
        const { dataUrl, width, height } = await shrink(file);
        const r = await api.addPhoto({ kind, ownerId, dataUrl, width, height });
        added.push({ id: r.id, caption: '', width, height, at: new Date().toISOString() });
      }
      setPhotos((now) => [...(now ?? []), ...added]);
    } catch (e) {
      setFailed((e as Error)?.message ?? 'That photo could not be added.');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const remove = async (id: string) => {
    setPhotos((now) => (now ?? []).filter((x) => x.id !== id));
    setOpen(null);
    try {
      await api.removePhoto(id);
    } catch {
      /* Put it back rather than pretending it went. */
      const r = await api.listPhotos(kind, ownerId).catch(() => ({ photos: [] as Photo[] }));
      setPhotos(r.photos);
    }
  };

  const has = (photos?.length ?? 0) > 0;
  if (!has && !canAdd) return null;

  return (
    <div className="photos">
      {label && (has || canAdd) && <span className="hint">{label}</span>}

      <div className="photorow">
        {(photos ?? []).map((ph) => (
          <button
            key={ph.id}
            type="button"
            className="thumb"
            onClick={(e) => { e.stopPropagation(); setOpen(ph); }}
            aria-label={t('p_open')}
          >
            <img src={photoUrl(ph.id)} alt={ph.caption || 'Photo'} loading="lazy" />
          </button>
        ))}

        {canAdd && (
          <button
            type="button"
            className="thumb add"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); input.current?.click(); }}
          >
            <Icon name="camera" />
            <span>{busy ? 'Adding…' : 'Add'}</span>
          </button>
        )}
      </div>

      {failed && <span className="hint" style={{ color: 'var(--bad)' }}>{failed}</span>}

      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => void add(e.target.files)}
      />

      {open && (
        <PhotoViewer
          photo={open}
          onClose={() => setOpen(null)}
          onRemove={canAdd ? () => void remove(open.id) : undefined}
        />
      )}
    </div>
  );
}

/** One photo, full size. Escape closes it, as does the backdrop. */
export function PhotoViewer({
  photo, onClose, onRemove,
}: { photo: Photo; onClose: () => void; onRemove?: () => void }) {
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [onClose]);

  return (
    <div className="lightbox" onClick={onClose} role="presentation">
      <img
        src={photoUrl(photo.id)}
        alt={photo.caption || 'Photo'}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="lightbox-bar" onClick={(e) => e.stopPropagation()} role="presentation">
        {onRemove && (
          <button type="button" className="btn danger sm" onClick={onRemove}>
            <Icon name="trash" /> Remove
          </button>
        )}
        <button type="button" className="btn quiet sm" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- a profile face */

/** The one picture a cleaner is allowed to add, and the same control a home
 *  uses for its own. Replaces rather than stacks: a profile has one face. */
export function ProfilePhoto({
  kind, ownerId, current, onChanged,
}: {
  kind: 'profile-house' | 'profile-cleaner';
  ownerId: string;
  current: string | null;
  onChanged: (id: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const input = useRef<HTMLInputElement>(null);

  const pick = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    setFailed('');
    try {
      const { dataUrl, width, height } = await shrink(file);
      const r = await api.addPhoto({ kind, ownerId, dataUrl, width, height });
      onChanged(r.id);
    } catch (e) {
      setFailed((e as Error)?.message ?? 'That picture could not be used.');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const clear = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await api.removePhoto(current);
      onChanged(null);
    } catch (e) {
      setFailed((e as Error)?.message ?? 'That could not be removed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="row" style={{ gap: 14 }}>
      <div className="profilepic">
        {current
          ? <img src={photoUrl(current)} alt="" />
          : <Icon name="camera" />}
      </div>
      <div className="col grow" style={{ gap: 7 }}>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn ghost sm"
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {busy ? 'Working…' : current ? 'Change picture' : 'Add a picture'}
          </button>
          {current && (
            <button type="button" className="btn quiet sm" disabled={busy} onClick={clear}>
              Remove
            </button>
          )}
        </div>
        <span className="hint">
          {kind === 'profile-cleaner'
            ? 'A photo of you. Homes see this beside your name — a face gets answered faster than initials.'
            : 'A picture for your home. Only cleaners you are working with see it.'}
        </span>
        {failed && <span className="hint" style={{ color: 'var(--bad)' }}>{failed}</span>}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => void pick(e.target.files)}
      />
    </div>
  );
}

/** Photos already sent inside a chat message. Read-only: what is in a
 *  conversation stays in it. */
export function MessagePhotos({ ids }: { ids: string[] }) {
  const [open, setOpen] = useState<string | null>(null);
  if (ids.length === 0) return null;
  return (
    <>
      <div className="bubble-photos">
        {ids.map((id) => (
          <button key={id} type="button" onClick={() => setOpen(id)} aria-label={t('p_open')}>
            <img src={photoUrl(id)} alt="" loading="lazy" />
          </button>
        ))}
      </div>
      {open && (
        <PhotoViewer
          photo={{ id: open, caption: '', width: 0, height: 0, at: '' }}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}
